import { createRouter, createWebHistory } from "vue-router";
import axios from "axios";
import { activeModules } from "./module-loader";
import { AUTH_URL } from "@/config";
import { resolveMenuCode } from "@/constants/menuCodes";
import { logEvent } from "@/services/logger";
import { chatDisConnect } from "@/services/socket";
import store from "@/store";
import { saveRedirect } from "@/utils/authRedirect";

// 인증/런치 화면 (Core)
import Login from "@/views/MainLogin";
import Launch from "@/views/Launch";

// Sign up (Core)
import JoinAgree from "@/views/signUp/Agree";
import SelfAuth from "@/views/signUp/SelfAuth";
import InputInfo from "@/views/signUp/InputInfo";
import FinishJoin from "@/views/signUp/FinishJoin";
import ForgotPassword from "@/views/signUp/ForgotPassword";
import ResetPassword from "@/views/signUp/ResetPassword";

// 레이아웃 (Header/Content/SideNav)
import AppShell from "@/layout/AppShell";
import DashboardHome from "@/layout/DashboardHome";

const requireAuth = () => (from, to, next) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    return next();
  }
  next("/login");
};

let requireLogout = () => (to, from, next) => {
  const token = localStorage.getItem("accessToken");
  if (token !== null) {
    router.replace("/");
    return;
  }
  next();
};

// 모듈 라우트 취합 (각 모듈 매니페스트의 routes)
const moduleRoutes = activeModules.flatMap((m) => m.routes || []);

// 로그인 후 랜딩 경로 — 부여된 모듈(grantedApps) 우선순위대로 해당 모듈 메인으로.
// Remote/Check = 각 모듈 대시보드, 이력관리 = orders 메뉴. 없으면 /dashboard.
// (순환 import 방지: 가드와 동일하게 persisted vuex 에서 직접 읽음)
const MODULE_HOME = [
  { app: "REMOTE_APP", path: "/remote/dashboard" },
  { app: "HISTORY_WEB", path: "/history/orders" },
  { app: "CHECK_WEB", path: "/check/dashboard" },
];
function landingRoute() {
  // 미로그인 상태로 root(/) 진입 시엔 returnUrl 없이 바로 로그인 페이지로.
  // (/login 은 공개 경로라 인증 가드가 returnUrl 을 붙이지 않음)
  if (!localStorage.getItem("accessToken")) {
    return "/login";
  }
  let granted = [];
  try {
    granted =
      JSON.parse(localStorage.getItem("vuex") || "{}")?.loginStore
        ?.grantedApps || [];
  } catch {
    granted = [];
  }
  const hit = MODULE_HOME.find((m) => granted.includes(m.app));
  return hit ? hit.path : "/dashboard";
}

const routes = [
  // 레이아웃 없는 페이지 (로그인, 런치, 회원가입)
  {
    path: "/login",
    name: "Login",
    component: Login,
    beforeEnter: requireLogout(),
  },
  {
    // 외부 시스템 → 플랫폼 진입 (launch token 교환). 필요 없으면 제거 가능.
    path: "/launch",
    name: "Launch",
    component: Launch,
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword,
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: ResetPassword,
  },
  {
    // 외부(유니티 등) 계정 토큰 링크 진입 — /account?validate=... 가 이리로 리다이렉트됨.
    // account-validate 로 단기 JWT 교환 후 프로필 수정. 로그인 세션과 무관한 공개 standalone 페이지.
    path: "/account-link",
    name: "AccountLink",
    component: () => import("@/views/account/AccountLink.vue"),
  },
  {
    path: "/signup",
    name: "JoinAgree",
    component: JoinAgree,
  },
  {
    path: "/signup/selfauth",
    name: "SelfAuth",
    component: SelfAuth,
  },
  {
    path: "/signup/inputInfo",
    name: "InputInfo2",
    component: InputInfo,
  },
  {
    path: "/signup/finishjoin",
    name: "FinishJoin",
    component: FinishJoin,
  },

  // 레이아웃 있는 페이지 (Header + SideNav + Content)
  {
    path: "/",
    redirect: () => landingRoute(),
    component: AppShell,
    children: [
      {
        path: "/dashboard",
        name: "Dashboard",
        component: DashboardHome,
        beforeEnter: requireAuth(),
      },
      // 각 모듈(remote/history) 라우트가 Content(router-view) 에 렌더됨
      ...moduleRoutes,

      // 내 계정 (프로필/비밀번호/PIN/회원탈퇴) — 프로필 드롭다운에서만 진입, 사이드메뉴 미노출
      {
        path: "/account",
        name: "AccountSettings",
        component: () => import("@/views/account/AccountSettings.vue"),
        meta: { title: "내 계정" },
        beforeEnter: requireAuth(),
      },

      // 관리자 전용 (isAdmin 계정만 — 아래 beforeEach 가드에서 meta.admin 체크)
      // 권한 관리 라우트 숨김 (메뉴 단위 권한 미구현 — 모듈 단위는 가입 시 DB 연동됨)
      // {
      //   path: "/admin/permissions",
      //   name: "AdminPermissions",
      //   component: () => import("@/views/admin/PermissionManagement.vue"),
      //   meta: { title: "권한 관리", admin: true },
      //   beforeEnter: requireAuth(),
      // },
      {
        path: "/admin/logs",
        name: "AdminLogs",
        component: () => import("@/views/admin/LogManagement.vue"),
        meta: { title: "로그 관리", admin: true }, // ADMIN+SYSTEM (ADMIN은 서버측에서 자기 소속만 조회)
        beforeEnter: requireAuth(),
      },
      {
        path: "/admin/backup",
        name: "AdminBackup",
        component: () => import("@/views/admin/BackupRestore.vue"),
        meta: { title: "백업/복구", admin: true },
        beforeEnter: requireAuth(),
      },
      {
        path: "/admin/users",
        name: "AdminUserManagement",
        component: () => import("@/views/admin/UserManagement.vue"),
        meta: { title: "사용자 관리", admin: true },
        beforeEnter: requireAuth(),
      },
      {
        // 설비 모니터링 (PI Vision) — 2탭(부안/대전금고) iframe 임베드
        path: "/admin/pivision",
        name: "AdminPiVision",
        component: () => import("@/views/admin/PiVisionView.vue"),
        meta: { title: "설비 모니터링", admin: true },
        beforeEnter: requireAuth(),
      },
      {
        // 메뉴 관리 (menu_matrix CRUD — 메뉴 정의만)
        path: "/admin/menus",
        name: "AdminMenus",
        component: () => import("@/views/admin/MenuManagement.vue"),
        meta: { title: "메뉴 관리", admin: true },
        beforeEnter: requireAuth(),
      },
      {
        // 메뉴 권한 (permission_menu — 역할별 메뉴 노출 매트릭스)
        path: "/admin/menu-perms",
        name: "AdminMenuPerms",
        component: () => import("@/views/admin/MenuPermission.vue"),
        meta: { title: "메뉴 권한", admin: true },
        beforeEnter: requireAuth(),
      },
      // 라이선스 관리 라우트 숨김 (메뉴와 함께 비활성화)
      // {
      //   path: "/admin/billing",
      //   name: "AdminBilling",
      //   component: () => import("@/views/admin/Billing.vue"),
      //   meta: { title: "라이선스 관리", admin: true },
      //   beforeEnter: requireAuth(),
      // },
      {
        path: "/admin/security",
        name: "AdminSecurity",
        component: () => import("@/views/admin/SecurityManagement.vue"),
        meta: { title: "보안 관리", admin: true },
        beforeEnter: requireAuth(),
      },
    ],
  },
];

const router = new createRouter({
  history: createWebHistory(process.env.BASE_URL), // publicPath(=/rmt/ 등)에 맞춰 라우터 base 설정
  routes,
  linkActiveClass: "active",
  linkExactActiveClass: "exact-active",
});

// 공개 페이지 목록 (인증 불필요)
const publicPaths = [
  "/login",
  "/launch",
  "/signup",
  "/signup/selfauth",
  "/signup/inputInfo",
  "/signup/finishjoin",
  "/forgot-password",
  "/reset-password",
  "/account-link",
];

// 전역 네비게이션 가드
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem("accessToken");
  const currentPath = to.path.toLowerCase();

  // 외부(유니티) 계정 토큰 링크: /account?validate=... → 공개 standalone 페이지로.
  // (로그인 세션과 무관하게 처리해야 하므로 인증 체크 전에 분기, query 보존)
  if (currentPath === "/account" && to.query.validate) {
    next({ path: "/account-link", query: to.query, replace: true });
    return;
  }
  const isPublicPath =
    to.meta?.public === true ||
    publicPaths.some((path) => path.toLowerCase() === currentPath);

  // (소켓은 앱 전역 알림 버스로 승격 — 로그인 시 연결, 로그아웃 시에만 해제.
  //  더 이상 /remote 이탈 시 끊지 않는다. 연결/해제는 loginStore·부팅에서 관리.)

  // 로그인 안 된 상태에서 공개 페이지가 아니면 로그인으로
  // (원래 위치는 sessionStorage 에 저장 → 로그인 후 복귀. URL 에 returnUrl 노출 안 함)
  if (!token && !isPublicPath) {
    saveRedirect(to.fullPath);
    next({ path: "/login", replace: true });
    return;
  }
  // 로그인 된 상태에서 로그인 페이지 접근 시 기본 화면으로
  if (token && currentPath === "/login") {
    next("/history/orders");
    return;
  }

  // 비밀번호 만료(변경주기 초과) — 변경 전까지 /account 로 강제 (소프트 강제)
  if (token && currentPath !== "/account") {
    let pwExpired = false;
    try {
      pwExpired = !!JSON.parse(localStorage.getItem("vuex") || "{}")?.loginStore
        ?.userInfo?.PasswordExpired;
    } catch {
      pwExpired = false;
    }
    if (pwExpired) {
      next("/account");
      return;
    }
  }

  // 모듈 권한 체크 — 라우트 meta.perm 이 계정 grantedApps 에 없으면 차단
  // (순환 import 방지를 위해 persisted vuex 에서 직접 읽음)
  const perm = to.meta?.perm;
  if (token && perm) {
    let granted = [];
    try {
      granted =
        JSON.parse(localStorage.getItem("vuex") || "{}")?.loginStore
          ?.grantedApps || [];
    } catch {
      granted = [];
    }
    if (!granted.includes(perm)) {
      // 게스트 링크 참여자 예외: 모듈 권한이 없어도 협업 Zone(/remote/zone) 진입만 허용.
      // (링크 토큰이 인가. Remote 모듈 전체 = 대시보드·멤버 등은 여전히 차단)
      const guestZone =
        perm === "REMOTE_APP" &&
        currentPath.startsWith("/remote/zone") &&
        sessionStorage.getItem("remoteGuestAccess") === "true";
      if (!guestZone) {
        next("/dashboard");
        return;
      }
    }
  }

  // 관리자 전용 라우트(meta.admin) — isAdmin 계정만 허용
  // meta.system 이 있으면 role=SYSTEM 계정만 허용 (ADMIN 도 차단)
  if (token && to.meta?.admin) {
    let userInfo = {};
    try {
      userInfo =
        JSON.parse(localStorage.getItem("vuex") || "{}")?.loginStore
          ?.userInfo || {};
    } catch {
      userInfo = {};
    }
    if (!userInfo.IsAdmin) {
      next("/dashboard");
      return;
    }
    if (to.meta?.system && !userInfo.IsSystem) {
      next("/dashboard");
      return;
    }
  }

  next();
});

// 메뉴 이동 시 세션 생존 확인(블로킹) — 이동 "전"에 Auth 가드로 세션 상태 검증.
// REVOKED/LOGOUT/EXPIRED 면 401 SESSION_INVALID → main.js 인터셉터가 /login 리다이렉트하고,
// 여기서 next(false)로 이동 자체를 차단(죽은 세션이면 대상 페이지를 아예 안 보여줌).
// 네트워크/서버 오류는 앱 잠금 방지를 위해 통과(fail-open). 매 이동마다 왕복 1회(쓰로틀 없음).
router.beforeEach(async (to, from, next) => {
  if (!localStorage.getItem("accessToken")) return next(); // 비로그인 제외
  try {
    await axios.get(`${AUTH_URL}/session/check`, { timeout: 5000 });
    next();
  } catch (err) {
    if (err?.response?.status === 401) {
      // session/check 의 401 = 사유 불문 세션 종료(만료·REVOKE·무효) → 직접 정리 후 로그인으로.
      // (인터셉터는 SESSION_INVALID 메시지만 처리하므로 토큰 만료 등 일반 401 도 여기서 확실히 이동)
      try {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        delete axios.defaults.headers.common.Authorization;
        chatDisConnect(); // 세션 종료 → 좀비 소켓 정리(모니터 미매핑 집계 제거)
        store.commit("logoutSuccess"); // loginStore(isLogin/userInfo)만 리셋 — theme 는 별도 path 라 보존
      } catch (e) {
        /* 정리 실패 무시 */
      }
      if (to.path === "/login") {
        next();
      } else {
        saveRedirect(to.fullPath); // 복귀 경로 저장(URL 노출 없이)
        next({ path: "/login", replace: true });
      }
    } else {
      next(); // 네트워크/서버 오류 → fail-open (앱 잠금 방지)
    }
  }
});

// 메뉴 이동 로그 (CORE_MENU_ACCESS) — 로그인 상태 + 규약에 정의된 메뉴 경로만, 직전과 같으면 dedupe.
// 모바일은 동일 { module, menu } 값을 /api/core/log/menu 로 직접 전송(같은 테이블).
let lastMenuLogKey = "";
router.afterEach((to) => {
  try {
    if (!localStorage.getItem("accessToken")) return; // 비로그인 제외
    const code = resolveMenuCode(to.path);
    if (!code) return; // 규약에 없는 경로(상세/팝업 등)는 제외
    const key = code.module + "/" + code.menu;
    if (key === lastMenuLogKey) return; // 직전과 동일 메뉴 → 중복 제거
    lastMenuLogKey = key;
    logEvent("CORE_MENU_ACCESS", {
      module: code.module,
      menu: code.menu,
      path: to.path,
    });
  } catch (e) {
    /* 로깅 실패는 무시 */
  }
});

export default router;
